import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getDayHistory, revertDay } from '../../services/itinerary/regenerationApi';

function formatTimestamp(value) {
  if (!value) return '';
  try {
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) return String(value);
    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return String(value);
  }
}

function extractHistoryLabel(entry) {
  const kind = typeof entry?.kind === 'string' ? entry.kind : 'unknown';
  if (kind === 'initial') return 'Initial generation';
  if (kind === 'regenerate') return 'Regenerated version';
  if (kind === 'revert') return 'Reverted version';
  return kind;
}

function extractInstruction(entry) {
  const instr = entry?.dayContent?.iteration?.instruction;
  return typeof instr === 'string' && instr.trim() ? instr.trim() : null;
}

function normalizeErrorMessage(error) {
  if (!error) return 'Failed to load history.';
  if (typeof error === 'string') return error;
  if (error?.details) {
    if (typeof error.details === 'string') return error.details;
    if (typeof error.details.message === 'string') return error.details.message;
    if (typeof error.details.error === 'string') return error.details.error;
  }
  if (error?.message) {
    if (error.message !== 'INTERNAL') {
      return error.message;
    }
  }
  return 'Failed to load history.';
}

export default function DayHistoryDrawer({
  isOpen,
  onClose,
  itineraryId,
  versionId,
  dayIndex,
  dayTitle,
  editToken,
  isPreviewMode = false,
  onRevertSuccess
}) {
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const [revertingId, setRevertingId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const title = dayTitle || `Day ${typeof dayIndex === 'number' ? dayIndex + 1 : ''}`.trim();

  const canLoad = useMemo(() => {
    return Boolean(isOpen && itineraryId && versionId && typeof dayIndex === 'number');
  }, [isOpen, itineraryId, versionId, dayIndex]);

  const load = useCallback(async () => {
    if (!canLoad) return;
    if (!editToken && !isPreviewMode) {
      setStatus('error');
      setError('This itinerary is view-only (missing edit token).');
      setEntries([]);
      return;
    }

      setStatus('loading');
      setError(null);
      try {
        const result = await getDayHistory({
          itineraryId,
          versionId,
          dayIndex,
          editToken,
          limit: 25
        });
        const nextEntries = Array.isArray(result?.entries) ? result.entries : [];
        setEntries(nextEntries);
        setStatus('ready');
      } catch (err) {
        console.error('[DayHistoryDrawer] Failed to load history', err);
        setError(normalizeErrorMessage(err));
        setEntries([]);
        setStatus('error');
      }
  }, [canLoad, editToken, isPreviewMode, itineraryId, versionId, dayIndex]);

  useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setEntries([]);
      setError(null);
      setRevertingId(null);
      setExpanded(new Set());
      return;
    }
    load();
  }, [isOpen, load]);

  const handleToggleExpand = useCallback((historyId) => {
    if (!historyId) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(historyId)) next.delete(historyId);
      else next.add(historyId);
      return next;
    });
  }, []);

  const handleRevert = useCallback(async (entry) => {
    if (!editToken && !isPreviewMode) {
      setError('Edit token is missing. This itinerary may be view-only.');
      return;
    }
    const historyId = entry?.historyId;
    if (!historyId) return;

    const ok = window.confirm(`Revert ${title} to this version?`);
    if (!ok) return;

    setError(null);
    setRevertingId(historyId);
    try {
      const result = await revertDay({
        itineraryId,
        versionId,
        dayIndex,
        historyId,
        editToken
      });
      onRevertSuccess?.(result);
      await load();
    } catch (err) {
      console.error('[DayHistoryDrawer] Revert failed', err);
      setError(err?.message || 'Failed to revert day.');
    } finally {
      setRevertingId(null);
    }
  }, [dayIndex, editToken, isPreviewMode, itineraryId, load, onRevertSuccess, title, versionId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={revertingId ? undefined : onClose}
      />

      <div className="relative w-full max-w-2xl rounded-t-lg bg-slate-900 shadow-2xl sm:rounded-lg">
        <div className="border-b border-white/10 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-white">History: {title}</h2>
              <p className="mt-1 text-sm text-white/60">
                Reverting does not consume your regeneration limit.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close"
              disabled={Boolean(revertingId)}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-6">
          {status === 'loading' && (
            <div className="py-6 text-center">
              <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-emerald-400/20 border-t-emerald-400" />
              <p className="text-sm text-white/70">Loading history…</p>
            </div>
          )}

          {status !== 'loading' && error && (
            <div className="mb-4 rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          {status === 'ready' && entries.length === 0 && (
            <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-4 text-sm text-white/70">
              No history yet. Regenerate this day to start version history.
            </div>
          )}

          {status === 'ready' && entries.length > 0 && (
            <div className="space-y-3">
              {entries.map((entry) => {
                const historyId = entry?.historyId;
                const label = extractHistoryLabel(entry);
                const timestamp = formatTimestamp(entry?.timestampISO || entry?.timestamp);
                const instruction = extractInstruction(entry);
                const isExpanded = historyId ? expanded.has(historyId) : false;
                const blocks = Array.isArray(entry?.dayContent?.blocks) ? entry.dayContent.blocks : [];
                const blockTitles = blocks
                  .slice(0, 10)
                  .map((block, idx) => block?.label || block?.intent || `Block ${idx + 1}`)
                  .filter(Boolean);

                return (
                  <div key={historyId || timestamp} className="rounded-lg border border-white/10 bg-white/5 px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-sm font-semibold text-white">{label}</span>
                          {timestamp && (
                            <span className="text-xs text-white/60">{timestamp}</span>
                          )}
                          {entry?.isOriginal && (
                            <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-300">
                              Original
                            </span>
                          )}
                        </div>
                        {instruction && (
                          <p className="mt-2 text-sm text-emerald-200/90">
                            “{instruction}”
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                        {blockTitles.length > 0 && (
                          <button
                            type="button"
                            onClick={() => handleToggleExpand(historyId)}
                            className="btn-secondary px-3 py-2 text-xs"
                            disabled={Boolean(revertingId)}
                          >
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRevert(entry)}
                          className="btn-primary-cta px-3 py-2 text-xs"
                          disabled={Boolean(revertingId)}
                        >
                          {revertingId === historyId ? 'Reverting…' : 'Revert to this'}
                        </button>
                      </div>
                    </div>

                    {isExpanded && blockTitles.length > 0 && (
                      <div className="mt-4 border-t border-white/10 pt-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                          Blocks
                        </p>
                        <ul className="mt-2 space-y-1 list-disc list-inside text-sm text-white/80">
                          {blockTitles.map((title, idx) => (
                            <li key={`${historyId}-${idx}`}>{title}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={Boolean(revertingId)}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
